import { config } from '../../config';
import { useLogger } from './LoggerProvider';
import { useAuth } from '@presov.sk/presov-auth-react';
import mqtt, { type MqttClient } from 'mqtt';
import React from 'react';

type MessageCallback = (message: string, topic: string) => void;

export type MqttClientContextType = {
  subscribe: (
    topic: string,
    callback: MessageCallback,
    opts?: mqtt.IClientSubscribeOptions,
  ) => () => void; // returns unsubscribe function
  disconnect?: () => void;
};

export const MqttClientContext = React.createContext<MqttClientContextType>({
  subscribe: () => {
    console.warn('MQTT client not initialized. Cannot subscribe to topic.');
    return () => {};
  },
  disconnect: () => {
    console.warn('MQTT client not initialized. Cannot disconnect.');
  },
});

const MqttClientProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { authUser, accessToken } = useAuth();
  const handlersRef = React.useRef<Record<string, MessageCallback>>({});
  const { logger } = useLogger();

  // Klient je v state (nie v useMemo/ref), aby sa po jeho vytvorení zmenila
  // identita `subscribe` a konzumenti sa prihlásili na odber.
  const [client, setClient] = React.useState<MqttClient | null>(null);

  const clientAccountId = authUser?.clientAccountId;

  React.useEffect(() => {
    if (!config.MQTT_URI || !clientAccountId || !accessToken) {
      return;
    }

    const mqttClient = mqtt.connect(config.MQTT_URI, {
      username: clientAccountId,
      password: accessToken,
      clientId: `moj.presov.sk-${clientAccountId}-${Math.random().toString(16).slice(2)}`,
      reconnectPeriod: 5000,
    });

    // Výpadok spojenia reportujeme na server len raz. Bez toho by reconnect
    // každých 5 sekúnd generoval nekonečnú sériu volaní na logger.
    let errorReported = false;

    const handleMessage = (topic: string, payload: Buffer) => {
      const handler = handlersRef.current[topic];
      if (handler) {
        handler(payload.toString(), topic);
      }
    };

    const handleConnect = () => {
      errorReported = false;
      console.log('MQTT client connected');
    };

    const handleError = (error: Error) => {
      console.error('MQTT client error:', error);

      if (errorReported) {
        return;
      }
      errorReported = true;

      logger.error('MQTT client error', {
        action: 'mqtt-connect',
        error,
        data: {
          uri: config.MQTT_URI,
          clientId: mqttClient.options.clientId,
        },
      });
    };

    const handleClose = () => {
      console.log('MQTT connection closed');
    };

    mqttClient.on('message', handleMessage);
    mqttClient.on('connect', handleConnect);
    mqttClient.on('error', handleError);
    mqttClient.on('close', handleClose);

    setClient(mqttClient);

    return () => {
      setClient(null);
      mqttClient.off('message', handleMessage);
      mqttClient.off('connect', handleConnect);
      mqttClient.off('error', handleError);
      mqttClient.off('close', handleClose);
      mqttClient.end(true);
    };
  }, [clientAccountId, accessToken, logger]);

  const subscribe = React.useCallback(
    (
      topic: string,
      callback: MessageCallback,
      opts?: mqtt.IClientSubscribeOptions,
    ) => {
      if (!client) {
        console.warn('MQTT client not initialized.');
        return () => {};
      }

      handlersRef.current[topic] = callback;

      client.subscribe(topic, opts || { qos: 1 }, (err) => {
        if (!err) {
          return;
        }

        console.error(`Failed to subscribe to ${topic}:`, err);

        // Ak spojenie nestojí, ide len o dôsledok výpadku - ten už reportuje
        // handler na 'error'. Neduplikujeme ho pre každý topic zvlášť.
        if (!client.connected) {
          return;
        }

        logger.error('Failed to subscribe to MQTT topic', {
          action: 'mqtt-subscribe',
          error: err,
          data: {
            topic,
          },
        });
      });

      // Return unsubscribe function
      return () => {
        delete handlersRef.current[topic];
        client.unsubscribe(topic, (err) => {
          if (!err) {
            console.log(`Unsubscribed from topic ${topic}`);
            return;
          }

          console.error(`Failed to unsubscribe from ${topic}:`, err);

          if (!client.connected) {
            return;
          }

          logger.error('Failed to unsubscribe from MQTT topic', {
            action: 'mqtt-unsubscribe',
            error: err,
            data: {
              topic,
            },
          });
        });
      };
    },
    [client, logger],
  );

  const disconnect = React.useCallback(() => {
    client?.end(true);
  }, [client]);

  const value: MqttClientContextType = React.useMemo(
    () => ({
      subscribe,
      disconnect,
    }),
    [subscribe, disconnect],
  );

  return (
    <MqttClientContext.Provider value={value}>
      {children}
    </MqttClientContext.Provider>
  );
};

export const useMqtt = () => React.useContext(MqttClientContext);

export default MqttClientProvider;
